import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, Search, X } from 'lucide-react'

import { api } from '@/api/client'
import type { PartnerGroup } from '@/types/models'
import { cn } from '@/lib/utils'

interface Props {
  value: number | null
  onChange: (g: PartnerGroup | null) => void
  placeholder?: string
  className?: string
}

/** 단일 상위 거래처 선택 — 타이핑 검색 인라인 드롭다운. */
export function SearchableGroupPicker({
  value, onChange, placeholder = '상위 거래처 검색...', className,
}: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [label, setLabel] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!value) { setLabel(''); return }
    if (label) return
    api<PartnerGroup>(`/api/partner-groups/${value}`)
      .then((g) => setLabel(`${g.name} · ${g.member_count}개`))
      .catch(() => {})
  }, [value, label])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false); setSearch('')
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const { data: groups = [] } = useQuery({
    queryKey: ['searchable-group-picker', search],
    queryFn: () => {
      const params = new URLSearchParams()
      if (search) params.set('q', search)
      return api<PartnerGroup[]>(`/api/partner-groups?${params}`)
    },
    enabled: open,
    staleTime: 30_000,
  })

  useEffect(() => { setActiveIdx(0) }, [search, open])

  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-picker-idx="${activeIdx}"]`,
    )
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx, open, groups.length])

  function select(g: PartnerGroup) {
    setLabel(`${g.name} · ${g.member_count}개`)
    onChange(g)
    setOpen(false); setSearch('')
    inputRef.current?.blur()
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setOpen(true)
      setActiveIdx((i) => Math.min(i + 1, Math.max(groups.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Home') {
      e.preventDefault(); setActiveIdx(0)
    } else if (e.key === 'End') {
      e.preventDefault(); setActiveIdx(Math.max(groups.length - 1, 0))
    } else if (e.key === 'Enter') {
      const g = groups[activeIdx]
      if (g) { e.preventDefault(); select(g) }
    } else if (e.key === 'Escape') {
      setOpen(false); setSearch(''); inputRef.current?.blur()
    }
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation()
    onChange(null); setLabel(''); setSearch('')
    inputRef.current?.focus()
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div className={cn(
        'flex items-center gap-1.5 px-2 py-1.5 border rounded-lg bg-white transition-colors',
        open ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-slate-300 hover:border-slate-400',
      )}>
        <Search className="w-3.5 h-3.5 text-slate-400 shrink-0"/>
        <input
          ref={inputRef}
          type="text"
          value={open ? search : label}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true) }}
          onFocus={() => { setOpen(true); setSearch('') }}
          onKeyDown={onKeyDown}
          placeholder={label ? '' : placeholder}
          className="flex-1 min-w-[80px] text-sm outline-none bg-transparent placeholder-slate-400"
        />
        {value && !open && (
          <button
            type="button"
            onClick={clear}
            className="p-0.5 text-slate-400 hover:text-rose-500 shrink-0"
            title="선택 해제"
          >
            <X className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-40 mt-1 left-0 right-0 min-w-[260px] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          <div ref={listRef} className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {groups.map((g, i) => {
              const selected = value === g.id
              const active = i === activeIdx
              return (
                <button
                  key={g.id} type="button"
                  data-picker-idx={i}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => select(g)}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm flex items-center gap-2',
                    active
                      ? 'bg-blue-100'
                      : selected
                        ? 'bg-blue-50'
                        : 'hover:bg-blue-50',
                  )}
                >
                  <Building2 className="w-3.5 h-3.5 text-blue-500 shrink-0"/>
                  <span className="font-medium truncate">{g.name}</span>
                  <span className="ml-auto text-[11px] text-slate-500 tabular-nums shrink-0">
                    {g.member_count}개 자식
                  </span>
                </button>
              )
            })}
            {!groups.length && (
              <div className="text-center text-slate-400 py-6 text-sm">
                {search ? '검색 결과 없음' : '입력하여 검색'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
